import { useParams } from "react-router-dom";
import { useQuery } from "urql";
import ArticleTable from "../Articles/components/ArticleTable/ArticleTable";
import Page from "../Page";
import urls from "../../routes";
import { ArticleStatus } from "../../__generated__/gql/graphql";
import { graphql } from "../../__generated__/gql";
import { useState } from "react";
import Badge from "../../ui/Badge/Badge";
import useFilters from "../../ui/FilterDrawer/useFilters";
import Tabs from "../../ui/Tabs/Tabs";
import styles from "./Pattern.module.scss";
import PatternName from "../NewPattern/components/PatternName";
import NotFound from "../NotFound/NotFound";

const PatternQuery = graphql(/* GraphQL */ `
  query pattern($id: String!) {
    pattern(id: $id) {
      id
      templateText
      templateNodes {
        type
        value
      }
      patternSymbols {
        name
        color
        values
      }
    }
  }
`);

const PatternArticleCountsQuery = graphql(/* GraphQL */ `
  query patternArticleCounts($patternId: String!) {
    articleCounts(input: { filters: [{ patternIds: [$patternId] }] }) {
      total
      draft
      generated
      published
      processing
    }
  }
`);

function Pattern() {
  const { id } = useParams<{ id: string }>();
  const [tableLoaded, setTableLoaded] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("all");
  const { filterGroups, deleteFilter, addFilter, editFilter, clearFilters } =
    useFilters({});
  const [patternResponse] = useQuery({
    query: PatternQuery,
    variables: { id: id! },
    pause: !id,
  });
  const [patternArticleCountsResponse] = useQuery({
    query: PatternArticleCountsQuery,
    variables: { patternId: id! },
    pause: !id,
  });

  if (!patternResponse.data?.pattern) {
    if (!patternResponse.fetching) {
      return <NotFound />;
    }

    return null;
  }

  return (
    <Page
      loading={
        patternResponse.fetching || !patternResponse.data || !tableLoaded
      }
      breadcrumbs={[
        {
          name: "Patterns",
          url: urls.patterns,
        },
      ]}
      fullHeight
      title={
        <PatternName
          symbols={patternResponse.data?.pattern.patternSymbols ?? []}
          templateNodes={patternResponse.data?.pattern.templateNodes ?? []}
        />
      }
      documentTitle={patternResponse.data?.pattern.templateText ?? ""}
    >
      <Tabs
        className={styles.container}
        value={activeTab}
        onValueChange={setActiveTab}
        tabs={[
          {
            id: "all",
            label: (
              <>
                <span>All</span>
                <Badge
                  showDot={false}
                  variant="badge"
                  color={activeTab === "all" ? "primary" : "default"}
                >
                  {patternArticleCountsResponse.data?.articleCounts.total}
                </Badge>
              </>
            ),
            content: (
              <ArticleTable
                patternId={id}
                filterGroups={filterGroups}
                deleteFilter={deleteFilter}
                addFilter={addFilter}
                editFilter={editFilter}
                clearFilters={clearFilters}
                onLoad={() => setTableLoaded(true)}
              />
            ),
          },
          {
            id: "generated",
            label: (
              <>
                <span>Generated</span>
                <Badge
                  showDot={false}
                  variant="badge"
                  color={activeTab === "generated" ? "primary" : "default"}
                >
                  {patternArticleCountsResponse.data?.articleCounts.generated}
                </Badge>
              </>
            ),
            content: (
              <ArticleTable
                patternId={id}
                status={ArticleStatus.Generated}
                filterGroups={filterGroups}
                deleteFilter={deleteFilter}
                addFilter={addFilter}
                editFilter={editFilter}
                clearFilters={clearFilters}
                onLoad={() => setTableLoaded(true)}
              />
            ),
          },
          {
            id: "drafted",
            label: (
              <>
                <span>Drafted</span>
                <Badge
                  showDot={false}
                  variant="badge"
                  color={activeTab === "drafted" ? "primary" : "default"}
                >
                  {patternArticleCountsResponse.data?.articleCounts.draft}
                </Badge>
              </>
            ),
            content: (
              <ArticleTable
                patternId={id}
                status={ArticleStatus.Draft}
                filterGroups={filterGroups}
                deleteFilter={deleteFilter}
                addFilter={addFilter}
                editFilter={editFilter}
                clearFilters={clearFilters}
                onLoad={() => setTableLoaded(true)}
              />
            ),
          },
          {
            id: "processing",
            label: (
              <>
                <span>Processing</span>
                <Badge
                  showDot={false}
                  variant="badge"
                  color={activeTab === "processing" ? "primary" : "default"}
                >
                  {patternArticleCountsResponse.data?.articleCounts.processing}
                </Badge>
              </>
            ),
            content: (
              <ArticleTable
                patternId={id}
                status={ArticleStatus.Processing}
                filterGroups={filterGroups}
                deleteFilter={deleteFilter}
                addFilter={addFilter}
                editFilter={editFilter}
                clearFilters={clearFilters}
                onLoad={() => setTableLoaded(true)}
              />
            ),
          },
          {
            id: "published",
            label: (
              <>
                <span>Published</span>
                <Badge
                  showDot={false}
                  variant="badge"
                  color={activeTab === "published" ? "primary" : "default"}
                >
                  {patternArticleCountsResponse.data?.articleCounts.published}
                </Badge>
              </>
            ),
            content: (
              <ArticleTable
                patternId={id}
                status={ArticleStatus.Published}
                filterGroups={filterGroups}
                deleteFilter={deleteFilter}
                addFilter={addFilter}
                editFilter={editFilter}
                clearFilters={clearFilters}
                onLoad={() => setTableLoaded(true)}
              />
            ),
          },
        ]}
      />
    </Page>
  );
}

export default Pattern;
